import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Menu } from "lucide-react";

/**
 * Responsive top-bar action row.
 * - `inlineExtras`: React node always visible (e.g. NotificationBell)
 * - `items`: [{ label, icon, onClick, testid, destructive }]  → collapsed to hamburger on mobile, inline on md+
 * - `title`: title shown in mobile sheet header
 */
export default function MobileNav({ inlineExtras, items = [], title = "القائمة" }) {
    const [open, setOpen] = useState(false);

    const handle = (fn) => {
        setOpen(false);
        setTimeout(() => fn && fn(), 100);
    };

    return (
        <div className="flex items-center gap-2">
            {inlineExtras}

            {/* Desktop: render items inline */}
            <div className="hidden md:flex items-center gap-2">
                {items.map((it, i) => {
                    const Icon = it.icon;
                    if (it.render) return <span key={i}>{it.render}</span>;
                    return (
                        <Button
                            key={i}
                            variant="ghost"
                            size="sm"
                            onClick={it.onClick}
                            data-testid={it.testid}
                            className={`text-white/80 hover:text-white hover:bg-white/10 ${it.destructive ? "hover:text-[#FCA5A5]" : ""}`}
                        >
                            {Icon ? <Icon className="w-4 h-4 ml-1" /> : null}
                            <span className="hidden lg:inline">{it.label}</span>
                        </Button>
                    );
                })}
            </div>

            {/* Mobile: hamburger */}
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="btn-mobile-menu"
                aria-label="فتح القائمة"
                className="md:hidden p-2 rounded-lg text-white/90 hover:bg-white/10 transition-colors"
            >
                <Menu className="w-6 h-6" />
            </button>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side="right" dir="rtl" className="w-72 bg-[#0F172A] border-r-white/10 text-white p-0 flex flex-col">
                    <SheetHeader className="p-5 border-b border-white/10 text-right">
                        <SheetTitle className="text-white text-base">{title}</SheetTitle>
                    </SheetHeader>
                    <div className="flex-1 p-3 space-y-1 overflow-y-auto">
                        {items.map((it, i) => {
                            const Icon = it.icon;
                            if (it.render) return <div key={i} className="px-2 py-1">{it.render}</div>;
                            return (
                                <button
                                    key={i}
                                    onClick={() => handle(it.onClick)}
                                    data-testid={it.testid ? `mobile-${it.testid}` : undefined}
                                    className={`w-full text-right flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
                                        it.destructive
                                            ? "text-[#FCA5A5] hover:bg-[#C8102E]/15"
                                            : "text-white/85 hover:bg-white/10 hover:text-white"
                                    }`}
                                >
                                    {Icon ? <Icon className="w-5 h-5 shrink-0" /> : null}
                                    <span className="flex-1">{it.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
}
